import React, { useEffect, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';

// components
import MainContainer from 'page_modules/business-page-containers/templates/main-container';
import ErrorView from './ErrorView';

// selectors and misc utils
import ajax from 'service_modules/xml-http-requests/card-shop-api-v1.ajax';
import {
  getPageGlobalData,
  getExperimentalPageData
} from 'src/universal/redux/page';
import { errorPageData } from './__data__';
import dtmEvents from 'service_modules/models/dtm-tagging';
import { logError } from 'src/universal/utils/IsomorphicLogger/logError';

// styles
import styles from './__styles__/index.scss?modules';

const ErrorPage = ({
  localGlobal,
  localTNL,
  countryCode,
  params,
  reqUrl,
  httpStatus,
  errorDetails,
  stackTrace,
  serverSideError,
  isPrequal
}) => {
  const pageEventDone = useRef(false);
  const [globalTnlData, setGlobalTnlData] = useState({
    globalData: localGlobal,
    TNL: localTNL
  });
  const getErrorCode = useCallback(
    () => {
      const errCodeVal = params?.errorCode || httpStatus || '500';
      return String(errCodeVal).trim();
    },
    [params, httpStatus]
  );

  const getExpiredEepVal = useCallback(
    () => {
      if (typeof reqUrl === 'string') {
        const matchResult = /\/open_cd\/\d{2,20}(\/)/.exec(reqUrl);
        if (matchResult && Array.isArray(matchResult)) {
          const resultVal = String(matchResult[0]);
          return resultVal.replace(/\/open_cd\//, '').replace(/\//g, '');
        }
      }
      return '';
    },
    [reqUrl]
  );

  const isSubNavNeeded = useCallback(() => {
    const errorCode = getErrorCode();
    return ['400', '417', '404'].indexOf(errorCode) > -1;
  });

  const firePageLoadEvent = useCallback(() => {
    const errCodesNeedEvt = ['404', '417', '500'];
    const errCode = getErrorCode();
    if (errCodesNeedEvt.indexOf(errCode) > -1 && !pageEventDone.current) {
      pageEventDone.current = true;
      dtmEvents.dispatch({
        type: 'DTM_DATA_READY',
        dtmTag: { pageName: `errorbusiness${errCode}` }
      });
      dtmEvents.dispatch({ type: 'DTM_PAGE_LOAD' });
    }
  });

  const isValidGlobalData = useCallback(data => {
    return !!data?.globalHeaderPage?.subNav;
  });

  const getStaticData = useCallback(
    () => {
      const backupCountryCode = 'en-us';
      const errorCode = getErrorCode();
      const tmpData = errorPageData[countryCode];
      const staticData = tmpData ? tmpData : errorPageData[backupCountryCode];
      let dataVal;

      switch (errorCode) {
        case '400':
        case '417':
          dataVal = staticData.errorOfferGatingData
            ? staticData.errorOfferGatingData
            : errorPageData[backupCountryCode].errorOfferGatingData;
          // per conv with Parag on feb-16, we need to provide EEP for propergating onto target vac page,
          dataVal.expiredEepVal = getExpiredEepVal();
          break;
        case '404':
          dataVal = staticData.error404Data;
          break;
        default:
          // 500, 503, anything else
          dataVal = staticData.error500Data;
          break;
      }
      return dataVal;
    },
    [countryCode]
  );

  useEffect(
    async () => {
      firePageLoadEvent();

      if (!serverSideError) {
        logError('client-side-error', {
          errorCode: getErrorCode(),
          errorDetails: errorDetails,
          stackTrace: stackTrace
        });
      }

      const needSubNav = isSubNavNeeded();
      const hasGlobalData = isValidGlobalData(globalTnlData.globalData);

      if (needSubNav && !hasGlobalData) {
        try {
          const reqResponse = await ajax.get('open/content/subnav');
          setGlobalTnlData({
            status: reqResponse?.status,
            currentPageType: 'error',
            globalData: reqResponse?.data?.globalData,
            TNL: reqResponse?.data?.TNL
          });
        } catch (error) {
          console.error(error);
        }
      }
    },
    [params, httpStatus, serverSideError]
  );

  const errView = (
    <div className={styles.errorPage}>
      <ErrorView data={getStaticData()} />
    </div>
  );

  return isSubNavNeeded() && isValidGlobalData(globalTnlData.globalData) ? (
    <MainContainer
      globalData={globalTnlData.globalData}
      serverData={globalTnlData.globalData}
      TNL={globalTnlData.TNL}
      showOffers={true}
      showPhoneNumber={isPrequal}
      countryCode={countryCode}
      className={styles.errorPageOuter}
    >
      {errView}
    </MainContainer>
  ) : (
    errView
  );
};

const mapStateToProps = state => {
  return {
    localGlobal: getPageGlobalData(state),
    localTNL: getExperimentalPageData(state)
  };
};

export default connect(mapStateToProps)(React.memo(ErrorPage));
